import { useCallback } from 'react';

/**
 * Hook to automatically fill user_name and family_name fields based on user_id/email
 * @param {Object} formData - Current form data
 * @param {Function} setFormData - Function to update form data
 * @returns {Function} - Handler function to call on blur of user_id field
 */
export function useAutoFillUserInfo(formData, setFormData) {
  /**
   * Extracts user_name and family_name from email address
   * Handles multiple parts: e.g., "john.t.doe@example.com" -> "John T Doe" / "Doe"
   * @param {string} email - Email address (e.g., "john.doe@example.com" or "john.t.doe@example.com")
   * @returns {Object} - { user_name: string, family_name: string }
   */
  const extractUserInfoFromEmail = useCallback((email) => {
    if (!email || typeof email !== 'string') {
      return { user_name: '', family_name: '' };
    }

    // Extract the local part (before @)
    const localPart = email.split('@')[0];
    
    if (!localPart) {
      return { user_name: '', family_name: '' };
    }

    // Split by dots, underscores, or hyphens to handle multiple name parts
    // Examples: "john.doe" -> ["john", "doe"]
    //           "john.t.doe" -> ["john", "t", "doe"]
    //           "mary-jane-smith" -> ["mary", "jane", "smith"]
    const parts = localPart.split(/[._-]/).filter(Boolean);
    
    if (parts.length === 0) {
      return { user_name: '', family_name: '' };
    }

    // Capitalize first letter of each part
    const capitalizedParts = parts.map(
      part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    );

    // Handle multiple parts: use last part as family_name, all others as first/middle names
    // Examples:
    //   ["John", "Doe"] -> user_name: "John Doe", family_name: "Doe"
    //   ["John", "T", "Doe"] -> user_name: "John T Doe", family_name: "Doe"
    //   ["Mary", "Jane", "Smith"] -> user_name: "Mary Jane Smith", family_name: "Smith"
    if (capitalizedParts.length >= 2) {
      const familyName = capitalizedParts[capitalizedParts.length - 1];
      // Join all parts except the last one (first name + any middle names/initials)
      const firstAndMiddleNames = capitalizedParts.slice(0, -1).join(' ');
      return {
        user_name: `${firstAndMiddleNames} ${familyName}`,
        family_name: familyName,
      };
    } else {
      // Single part: use as user_name only
      return {
        user_name: capitalizedParts[0],
        family_name: '',
      };
    }
  }, []);

  /**
   * Handler to call when user_id field loses focus
   * Only fills fields if they are currently empty
   */
  const handleUserIdBlur = useCallback(() => {
    const email = formData.user_id?.trim();
    
    if (!email) {
      return;
    }

    // Only auto-fill if the fields are currently empty
    const shouldFillUserName = !formData.user_name || formData.user_name.trim() === '';
    const shouldFillFamilyName = !formData.family_name || formData.family_name.trim() === '';

    if (!shouldFillUserName && !shouldFillFamilyName) {
      // Both fields already have values, don't overwrite
      return;
    }

    const { user_name, family_name } = extractUserInfoFromEmail(email);

    // Only update fields that are empty
    const updates = {};
    if (shouldFillUserName && user_name) {
      updates.user_name = user_name;
    }
    if (shouldFillFamilyName && family_name) {
      updates.family_name = family_name;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [formData.user_id, formData.user_name, formData.family_name, extractUserInfoFromEmail, setFormData]);

  return handleUserIdBlur;
}
